import { useMemo } from 'react';

import {
  AssignmentType, CompleteCheckoutInput, CreateRegistrationInput, ParticipantInput, ProductType,
} from '__generated__/graphql';
import {
  Event, Product, Session, Ticket, getKeyedSellables, getTicketsForSale, getVisibleProductsForTicketPromotion,
} from './helpers';

export interface PersonalisationInfo {
  /** The list of registrations that may be personalised, a subset of form.registrations.create */
  personalisableRegistrations: CreateRegistrationInput[];
  /** The registration that is currently being personalised */
  activeRegistration?: CreateRegistrationInput;
  /** The index of the registration that is currently being personalised */
  activeIndex: number;
  /** The index of the previous personalisable registration (compared to activeIndex) */
  prevIndex: number;
  /** The index of the next personalisable registration (compared to activeIndex) */
  nextIndex: number;
  /** The index of the first personalisable registration */
  firstIndex: number;
  /** The index of the last personalisable registration */
  lastIndex: number;
  /** A list of booleans indicating which of the personalisable registrations are skipped */
  skips: boolean[];
  /** A list of indices indicating which registrations in the form are personalisable (skipped or not) */
  personalisableFormIndices: number[];
  /** A list of indices indicating which registrations in the form are personalisable and NOT skipped */
  personalisedFormIndices: number[];
  /** The index of the registration in the form that is currently being personalised */
  activeFormIndex: number;
  /** The details of the person placing the order */
  buyer: ParticipantInput;
  /** The registrations that are assigned to the buyer */
  personalRegistrations: CreateRegistrationInput[];
  /** Indicates whether personalisation is required globally (for all tickets, regardless of assignment policy) */
  requirePersonalisationGlobally: boolean;
}

interface PersonalisationInfoProps {
  form: CompleteCheckoutInput;
  event: Event;
  session: Session;
  hasPreselectedCharity: boolean;
}

const usePersonalisationInfo = ({ form, event, session, hasPreselectedCharity }: PersonalisationInfoProps) => (
  useMemo(
    () => getPersonalisationInfo(
      form.registrations.create, form.participant, session.activeRegistration, event, hasPreselectedCharity,
    ),
    [form.registrations.create, form.participant, session.activeRegistration, event, hasPreselectedCharity],
  )
);

const getPersonalisationInfo = (
  allRegistrations: CreateRegistrationInput[],
  buyer: ParticipantInput,
  activeRegistrationIndex: number,
  event: Event,
  hasPreselectedCharity: boolean,
): PersonalisationInfo => {
  const requirePersonalisationGlobally = hasPreselectedCharity;

  /** Indices of registrations in allRegistrations that are personalisable */
  const personalisableFormIndices = getPersonalisableRegistrationIndices(
    allRegistrations,
    event,
    requirePersonalisationGlobally,
  );

  /** Personalisable registrations */
  const personalisableRegistrations = personalisableFormIndices.map((index) => allRegistrations[index]);

  const tickets = getTicketsForSale(event);

  const { nextIndex, prevIndex, firstIndex, lastIndex, skips } = getPaginationInfo(
    personalisableRegistrations,
    activeRegistrationIndex,
    tickets,
    event.products_for_sale,
  );

  const personalisedFormIndices = personalisableFormIndices.filter((formIndex) => {
    const isSkipped = () => {
      const personalisableRegistrationIndex = personalisableFormIndices.findIndex((i) => i === formIndex);

      if (personalisableRegistrationIndex > -1) {
        return skips[personalisableRegistrationIndex];
      }

      return false;
    };

    return personalisableFormIndices.includes(formIndex) && !isSkipped();
  });

  /**
   * Get the registrations that are assigned - based on e-mail and name - to the buyer.
   */
  const personalRegistrations = allRegistrations.filter(({ participant }) => {
    const assignee = participant;

    if (assignee && buyer) {
      if (assignee.email && assignee.first_name && assignee.last_name) {
        return assignee.email.toLowerCase() === buyer.email?.toLowerCase()
        && assignee.first_name.toLowerCase() === buyer.first_name?.toLowerCase()
        && assignee.last_name.toLowerCase() === buyer.last_name?.toLowerCase();
      }
    }

    return false;
  });

  return {
    personalisableRegistrations,
    activeRegistration: personalisableRegistrations[activeRegistrationIndex],
    activeIndex: activeRegistrationIndex,
    nextIndex,
    prevIndex,
    firstIndex,
    lastIndex,
    skips,
    personalisableFormIndices,
    personalisedFormIndices,
    activeFormIndex: personalisableFormIndices[activeRegistrationIndex],
    personalRegistrations,
    buyer,
    requirePersonalisationGlobally,
  };
};

/**
 * Get the indices of registrations that are personalisable: registrations that are assignable or have products.
 */
const getPersonalisableRegistrationIndices = (
  registrations: CreateRegistrationInput[],
  event: Event,
  requirePersonalisationGlobally: boolean,
) => registrations.reduce<number[]>((result, registration, index) => {
  const { purchase: { promotion } } = registration;
  const tickets = getTicketsForSale(event);
  const ticket = getKeyedSellables(tickets)[promotion.id];

  // Check that the ticket exists, because it might disappear when an activated invitation code is unset
  if (ticket) {
    const products = getVisibleProductsForTicketPromotion(promotion, event.products_for_sale);

    if (ticket.assignment_type !== AssignmentType.afterward || products.length > 0 || requirePersonalisationGlobally) {
      return [...result, index];
    }
  }

  return result;
}, []);

/** Exported for testing purposes only */
export const getPersonalisableRegistrationIndicesForTesting = getPersonalisableRegistrationIndices;

/**
 * Returns the previous and next registration index taking into account potential skips.
 * Registrations of the same type are skipped when the first is not personalised.
 */
const getPaginationInfo = (
  registrations: CreateRegistrationInput[],
  activeIndex: number,
  tickets: Ticket[],
  products: Product[],
) => {
  const skips = getSkips(registrations, tickets, products);
  const nextIndex = activeIndex + 1 + [...skips, false].slice(activeIndex + 1).findIndex((skip) => !skip);
  const prevIndex = activeIndex - 1 - skips.slice(0, activeIndex).reverse().findIndex((skip) => !skip);
  const firstIndex = skips.findIndex((skip) => !skip);
  const lastIndex = skips.length - 1 - [...skips].reverse().findIndex((skip) => !skip);

  return {
    skips,
    nextIndex,
    prevIndex,
    firstIndex,
    lastIndex,
  };
};

/** Exported for testing purposes only */
export const getPaginationInfoForTesting = getPaginationInfo;

const getSkips = (registrations: CreateRegistrationInput[], tickets: Ticket[], products: Product[]) => {
  const keyedTickets = getKeyedSellables(tickets);
  const keyedProducts = getKeyedSellables(products);

  /**
   * A registration is empty (skippable) if:
   * 1. the registration is unassigned,
   * 2. the registration has no custom fields,
   * 3. the registration has no upgrades (except ticket fees).
   */
  const isEmptyRegistration = (registration: CreateRegistrationInput) => {
    const { purchase: { promotion } } = registration;
    const ticket = keyedTickets[promotion.id];

    /** Indicates whether there are (non ticket fee) products that are required with this ticket */
    const hasRequiredProducts = () => (
      // Ticket check because ticket might not exist anymore
      ticket && getVisibleProductsForTicketPromotion(promotion, products).filter(
        (product) => product.min_per_ticket > 0,
      ).length > 0
    );

    /** Indicates whether the participant has any (non ticket fee) upgrades */
    const hasNonTicketFeeUpgrades = () => registration.upgrades.filter((upgrade) => (
      // Product might not exist anymore
      keyedProducts[upgrade.purchase.promotion.id]
      && keyedProducts[upgrade.purchase.promotion.id].type !== ProductType.fee
    )).length > 0;

    return !registration.participant
      && (registration.fields?.length || 0) === 0
      && !hasRequiredProducts()
      && !hasNonTicketFeeUpgrades();
  };

  const isSimilarRegistration = (registration: CreateRegistrationInput, other: CreateRegistrationInput) => (
    registration.purchase.promotion.id === other.purchase.promotion.id
      && registration.time_slot?.id === other.time_slot?.id
  );

  const containsSimilarAssignedRegistration = (
    registration: CreateRegistrationInput, registrations: CreateRegistrationInput[],
  ) => (
    registrations.filter(
      (other) => isSimilarRegistration(registration, other)
        // Because registrations are assigned by default,
        // consider registration without participant details as unassigned.
        && (other.participant?.email || other.participant?.first_name || other.participant?.last_name),
    ).length > 0
  );

  const skips = [false];
  let prototype: CreateRegistrationInput;

  registrations.forEach((registration, index) => {
    if (index > 0) {
      if (!skips[index - 1]) {
        const prevRegistration = registrations[index - 1];

        if (isEmptyRegistration(prevRegistration)) {
          prototype = prevRegistration;
        } else {
          prototype = null;
        }
      }

      if (prototype) {
        skips[index] = isSimilarRegistration(registration, prototype)
          && !containsSimilarAssignedRegistration(registration, registrations.slice(index));
      } else {
        skips[index] = false;
      }
    }
  });

  return skips;
};

export default usePersonalisationInfo;
